/**
 * Invoice due-date classification.
 *
 * `invoices.due_date` is a `timestamp WITHOUT time zone` column holding the
 * tenant's calendar date. Staff enter "2026-09-01"; the API returns it as a
 * naive `2026-09-01T00:00:00` (no `Z`). It is a DATE, not an instant.
 *
 * Classifying it by parsing as UTC and comparing against a local-midnight
 * `Date` shifted every invoice by the UTC offset: on 2026-08-31 every invoice
 * due 2026-09-01 (`2026-09-01T00:00:00Z` = 8 PM Eastern on the 31st) fell
 * before local tomorrow and rendered in Billing History instead of Future
 * Invoices. Compare DATE PARTS instead.
 */

import { localTodayYmd, parseDateOnly } from "@mediatechgroup/calendar-date";

// Re-exported so existing importers keep working; @mediatechgroup/calendar-date is the owner.
export { localTodayYmd };

/** `YYYY-MM-DD` portion of a stored due_date, or null when unparseable. */
export function dueDateYmd(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(dueDate));
  return m ? m[1] : null;
}

/**
 * True when the invoice is due tomorrow or later (belongs in Future
 * Invoices, not Billing History). An unparseable due date is not future.
 */
export function isFutureInvoice(
  dueDate: string | null | undefined,
  todayYmd: string = localTodayYmd(),
): boolean {
  const ymd = dueDateYmd(dueDate);
  return ymd !== null && ymd > todayYmd;
}

/**
 * The description a draft gets from its due date: "2025-09-01" → "Invoice for
 * September". `new Date("2025-09-01")` is UTC midnight — Aug 31 in US zones —
 * so the SAVED description read "Invoice for August" (D8, 2026-09-24).
 */
export function draftInvoiceDescription(dueDate: string): string | null {
  const date = parseDateOnly(dueDate);
  if (!date) return null;
  return `Invoice for ${date.toLocaleString("en-US", { month: "long" })}`;
}
